import type { FieldError, Form, Transition, WorkflowDraft } from '@servicetim/shared';
import {
  Badge,
  Button,
  Checkbox,
  Input,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@servicetim/ui/components';
import { Plus, SlidersHorizontal, Trash2 } from 'lucide-react';
import { useId } from 'react';
import { errorsAt } from '../../projects/lib/field-errors';
import { CheckList } from '../../projects/ui/check-list';
import { InlineErrors } from '../../projects/ui/inline-errors';
import { ReorderButtons } from '../../projects/ui/reorder-buttons';
import { SimpleSelect } from '../../projects/ui/simple-select';
import {
  addTransition,
  moveTransition,
  removeTransition,
  renameTransition,
  ruleSummary,
  updateTransition,
} from './draft-utils';

export interface TransitionsTableProps {
  draft: WorkflowDraft;
  onChange: (draft: WorkflowDraft) => void;
  /** Forms of kind `transition`, offered as the optional screen. */
  forms: readonly Form[];
  errors: readonly FieldError[];
  onEditRules: (index: number) => void;
}

/** Editable transitions: name, key, from (any / create / statuses), to, form, rules, order, remove. */
export function TransitionsTable({
  draft,
  onChange,
  forms,
  errors,
  onEditRules,
}: TransitionsTableProps) {
  const statusOptions = draft.statuses.map((s) => ({ value: s.id, label: s.name }));
  return (
    <div className="space-y-3">
      <div className="overflow-x-auto rounded-lg border border-border bg-surface-raised">
        <Table>
          <caption className="sr-only">Transitions</caption>
          <TableHeader className="bg-surface-sunken">
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-24">Order</TableHead>
              <TableHead>Name</TableHead>
              <TableHead className="w-40">Key</TableHead>
              <TableHead className="w-56">From</TableHead>
              <TableHead className="w-44">To</TableHead>
              <TableHead className="w-44">Screen</TableHead>
              <TableHead>Rules</TableHead>
              <TableHead className="w-12" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {draft.transitions.map((t, index) => {
              const rowErrors = errorsAt(errors, `transitions.${index}`);
              return (
                <TableRow key={t.id}>
                  <TableCell>
                    <ReorderButtons
                      label={t.name}
                      onUp={
                        index > 0
                          ? () => onChange(moveTransition(draft, index, index - 1))
                          : undefined
                      }
                      onDown={
                        index < draft.transitions.length - 1
                          ? () => onChange(moveTransition(draft, index, index + 1))
                          : undefined
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      aria-label={`Transition ${index + 1} name`}
                      value={t.name}
                      maxLength={60}
                      onChange={(e) => onChange(renameTransition(draft, index, e.target.value))}
                      aria-invalid={rowErrors.length > 0 || undefined}
                    />
                    <InlineErrors errors={rowErrors} prefix={`transitions.${index}.`} />
                  </TableCell>
                  <TableCell>
                    <Input
                      aria-label={`Transition ${index + 1} key`}
                      value={t.key}
                      maxLength={40}
                      className="font-mono text-xs"
                      onChange={(e) =>
                        onChange(updateTransition(draft, index, { key: e.target.value }))
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <FromStatusesPicker
                      transition={t}
                      draft={draft}
                      onChange={(fromStatusIds) =>
                        onChange(updateTransition(draft, index, { fromStatusIds }))
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <SimpleSelect
                      aria-label={`Transition ${index + 1} target`}
                      value={t.toStatusId}
                      options={statusOptions}
                      onChange={(next) =>
                        next && onChange(updateTransition(draft, index, { toStatusId: next }))
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <SimpleSelect
                      aria-label={`Transition ${index + 1} screen`}
                      value={t.formId}
                      emptyLabel="No screen"
                      options={forms.map((f) => ({ value: f.id, label: f.name }))}
                      onChange={(next) =>
                        onChange(updateTransition(draft, index, { formId: next ?? null }))
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Button variant="outline" size="sm" onClick={() => onEditRules(index)}>
                      <SlidersHorizontal />
                      {ruleSummary(t)}
                    </Button>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Remove ${t.name}`}
                      onClick={() => onChange(removeTransition(draft, index))}
                    >
                      <Trash2 />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      <InlineErrors errors={errors.filter((e) => e.path === 'transitions')} />
      <Button
        variant="outline"
        onClick={() => onChange(addTransition(draft))}
        disabled={draft.transitions.length >= 300}
      >
        <Plus />
        Add transition
      </Button>
    </div>
  );
}

/** `null` = any status (global), `[]` = the create transition, otherwise the listed statuses. */
function FromStatusesPicker({
  transition,
  draft,
  onChange,
}: {
  transition: Transition;
  draft: WorkflowDraft;
  onChange: (fromStatusIds: string[] | null) => void;
}) {
  const id = useId();
  const from = transition.fromStatusIds;
  const names = new Map(draft.statuses.map((s) => [s.id, s.name]));
  const summary =
    from === null ? (
      <Badge variant="info">Any status</Badge>
    ) : from.length === 0 ? (
      <Badge variant="secondary">Create</Badge>
    ) : (
      <span className="truncate">{from.map((id) => names.get(id) ?? '?').join(', ')}</span>
    );
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="w-full justify-start font-normal"
          aria-label={`From statuses of ${transition.name}`}
        >
          {summary}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 space-y-3">
        <div className="flex items-center gap-2 text-sm">
          <Checkbox
            id={`${id}-any`}
            checked={from === null}
            onCheckedChange={(next) => onChange(next === true ? null : [])}
          />
          <Label htmlFor={`${id}-any`} className="font-normal">
            Available from any status (global)
          </Label>
        </div>
        {from !== null ? (
          <>
            <CheckList
              aria-label="From statuses"
              options={draft.statuses.map((s) => ({ value: s.id, label: s.name }))}
              value={from}
              onChange={onChange}
              className="sm:grid-cols-1"
            />
            <p className="text-fg-muted text-xs">
              No status selected = the create transition (must target the initial status).
            </p>
          </>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
